import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Snackbar
} from '@mui/material';
import {
  People as PeopleIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Payment as PayrollIcon,
  Schedule as TimeIcon,
  BeachAccess as LeaveIcon,
  Business as OfficeIcon,
  Home as RemoteIcon,
  Warehouse as WarehouseIcon,
  Engineering as FieldIcon,
  CheckCircle as ActiveIcon,
  Pause as InactiveIcon,
  Pause,
  Upload as UploadIcon
} from '@mui/icons-material';

import AddEmployeeDialog from './AddEmployeeDialog';
import EmployeeDetailsDialog from './EmployeeDetailsDialog';
import TerminateEmployeeDialog from './TerminateEmployeeDialog';
import AIEmployeeAssistant from '../ai/AIEmployeeAssistant';
import { useAuth, ProtectedComponent } from '../../contexts/AuthContext';
import { ImportWizard } from '../import';
import { employeesApi, getErrorMessage } from '../../api/services';
import { Employee } from '../../types/api';

interface EmployeeExtended extends Omit<Employee, 'employee_number'> {
  employee_number?: string;
  work_location?: string;
  employee_type?: string;
  annual_salary?: number;
  hourly_rate?: number;
  manager_id?: number | null;
  national_insurance_number?: string;
  direct_reports_count?: number;
}

// Mock data for development when API doesn't match
const mockEmployees: EmployeeExtended[] = [
  {
    id: 1,
    employee_number: "EMP001",
    first_name: "John",
    last_name: "Smith",
    email: "john.smith@company.com",
    phone: "+44 20 7123 4567",
    job_title: "Senior Developer",
    department: "IT",
    employment_status: "active",
    start_date: "2022-01-15",
    salary: 75000,
    work_location: "London",
    employee_type: "full-time",
    annual_salary: 75000,
    manager_id: null,
    national_insurance_number: "AB123456C",
    direct_reports_count: 3,
    created_at: "2022-01-15T00:00:00Z"
  }
];

const EmployeeManagement: React.FC = () => {
  const { user, canViewEmployeeDetails, hasPermission } = useAuth();
  const [employees, setEmployees] = useState<EmployeeExtended[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeExtended | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const data = await employeesApi.getAll();
      if (data.employees) {
        // Cast API response to EmployeeExtended
        setEmployees(data.employees.map((emp: any) => ({
          ...emp,
          employee_number: emp.employee_number || `EMP${emp.id.toString().padStart(3, '0')}`,
          work_location: emp.work_location || 'office',
          employee_type: emp.employee_type || 'full-time',
          annual_salary: emp.annual_salary || emp.salary,
          national_insurance_number: emp.national_insurance_number || '',
          direct_reports_count: emp.direct_reports_count || 0
        })) as EmployeeExtended[]);
        setSummary(data.summary);
      } else {
        // Fallback to mock data
        setEmployees(mockEmployees);
        setSummary({
          total_employees: 1,
          active_employees: 1,
          departments: 1,
          average_salary: 75000
        });
      }
    } catch (err) {
      console.error('Failed to fetch employees:', err);
      // Use mock data for development
      setEmployees(mockEmployees);
      setSummary({
        total_employees: 1,
        active_employees: 1,
        departments: 1,
        average_salary: 75000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, employee: EmployeeExtended) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmployee(employee);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEmployee(null);
  };

  const handleAddEmployee = async (employeeData: any) => {
    try {
      setLoading(true);

      // Make API call to create employee
      const result = await employeesApi.create(employeeData);
      const newEmployee = result.employee;

      // Update local state with the new employee from the database
      setEmployees(prev => [...prev, newEmployee as EmployeeExtended]);

      setSnackbar({
        open: true,
        message: `Employee ${newEmployee.first_name} ${newEmployee.last_name} added successfully!`,
        severity: 'success'
      });

      // Refresh the employee list to ensure consistency
      fetchEmployees();

    } catch (error) {
      console.error('Error adding employee:', error);
      setSnackbar({
        open: true,
        message: `Failed to add employee: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmployee = async (updatedEmployee: EmployeeExtended) => {
    try {
      setLoading(true);

      // Make API call to update employee
      const result = await employeesApi.update(updatedEmployee.id, updatedEmployee);
      const updatedEmployeeFromDB = result.employee;

      // Update local state with the updated employee from the database
      setEmployees(prev => prev.map(emp =>
        emp.id === updatedEmployeeFromDB.id ? updatedEmployeeFromDB as EmployeeExtended : emp
      ));

      setSnackbar({
        open: true,
        message: `Employee ${updatedEmployeeFromDB.first_name} ${updatedEmployeeFromDB.last_name} updated successfully!`,
        severity: 'success'
      });

    } catch (error) {
      console.error('Error updating employee:', error);
      setSnackbar({
        open: true,
        message: `Failed to update employee: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmployee = async (employeeId: number) => {
    try {
      setLoading(true);
      const employee = employees.find(emp => emp.id === employeeId);

      // Make API call to delete employee
      await employeesApi.delete(employeeId);

      // Update local state by removing the employee
      setEmployees(prev => prev.filter(emp => emp.id !== employeeId));

      setSnackbar({
        open: true,
        message: `Employee ${employee?.first_name} ${employee?.last_name} deleted successfully!`,
        severity: 'success'
      });

      setDetailsDialogOpen(false);

    } catch (error) {
      console.error('Error deleting employee:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete employee: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTerminateEmployee = (terminationData: any) => {
    const employee = employees.find(emp => emp.id === terminationData.employee_id);
    setEmployees(prev => prev.map(emp =>
      emp.id === terminationData.employee_id
        ? { ...emp, employment_status: 'terminated', end_date: terminationData.termination_date }
        : emp
    ));
    setSnackbar({
      open: true,
      message: `Employment terminated for ${employee?.first_name} ${employee?.last_name}`,
      severity: 'success'
    });
    setTerminateDialogOpen(false);
  };

  const handleViewDetails = (employee: EmployeeExtended) => {
    // Temporarily disabled permission check for testing
    // if (!canViewEmployeeDetails(employee.id)) {
    //   setSnackbar({
    //     open: true,
    //     message: 'You do not have permission to view this employee\'s details',
    //     severity: 'error'
    //   });
    //   return;
    // }

    setSelectedEmployee(employee);
    setDetailsDialogOpen(true);
    // Don't call handleMenuClose() here - it clears selectedEmployee!
    // The menu will close automatically when dialog opens
  };

  const getLocationIcon = (location: string) => {
    switch (location) {
      case 'office': return <OfficeIcon />;
      case 'remote': return <RemoteIcon />;
      case 'warehouse': return <WarehouseIcon />;
      case 'field': return <FieldIcon />;
      default: return <OfficeIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'on_leave': return 'warning';
      case 'terminated': return 'error';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'permanent': return 'primary';
      case 'contract': return 'secondary';
      case 'temporary': return 'warning';
      case 'apprentice': return 'info';
      default: return 'default';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const TabPanel = ({ children, value, index }: any) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ p: 3, pt: 2 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4,
        mt: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Employee Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your workforce with UK compliance built-in
          </Typography>
        </Box>
        <ProtectedComponent permission="edit_all_employees">
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setShowImportDialog(true)}
              size="large"
            >
              Import Employees
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              size="large"
              sx={{ minWidth: 160 }}
            >
              Add Employee
            </Button>
          </Box>
        </ProtectedComponent>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <PeopleIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" color="primary">
                  {summary.total_employees}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Employees
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>By Location</Typography>
                <List dense>
                  {Object.entries(summary.by_location).map(([location, count]) => (
                    <ListItem key={location} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {getLocationIcon(location)}
                      </ListItemIcon>
                      <ListItemText
                        primary={`${location.charAt(0).toUpperCase() + location.slice(1)}: ${count}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>By Department</Typography>
                <List dense>
                  {Object.entries(summary.by_department).map(([dept, count]) => (
                    <ListItem key={dept} sx={{ py: 0.5 }}>
                      <ListItemText primary={`${dept}: ${count}`} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>By Type</Typography>
                <List dense>
                  {Object.entries(summary.by_type).map(([type, count]) => (
                    <ListItem key={type} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={type.charAt(0).toUpperCase() + type.slice(1)}
                              size="small"
                              color={getTypeColor(type) as any}
                            />
                            <Typography variant="body2">{String(count)}</Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label="All Employees" />
          <Tab label="Payroll" />
          <Tab label="Time Tracking" />
          <Tab label="Leave Management" />
          <Tab label="AI Assistant" />
        </Tabs>
      </Paper>

      {/* Employee List */}
      <TabPanel value={selectedTab} index={0}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Salary/Rate</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map((employee) => (
                <TableRow
                  key={employee.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleViewDetails(employee)}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {employee.first_name} {employee.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {employee.employee_number} • {employee.job_title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {employee.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{employee.department}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getLocationIcon(employee.work_location)}
                      <Typography variant="body2">
                        {employee.work_location.charAt(0).toUpperCase() + employee.work_location.slice(1)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={employee.employee_type.charAt(0).toUpperCase() + employee.employee_type.slice(1)}
                      size="small"
                      color={getTypeColor(employee.employee_type) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={employee.employment_status.charAt(0).toUpperCase() + employee.employment_status.slice(1)}
                      size="small"
                      color={getStatusColor(employee.employment_status) as any}
                      icon={employee.employment_status === 'active' ? <ActiveIcon /> : <InactiveIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    {employee.annual_salary
                      ? formatCurrency(employee.annual_salary) + '/year'
                      : employee.hourly_rate
                        ? formatCurrency(employee.hourly_rate) + '/hour'
                        : 'N/A'
                    }
                  </TableCell>
                  <TableCell>{formatDate(employee.start_date)}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent row click
                        handleMenuClick(e, employee);
                      }}
                    >
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Payroll Tab */}
      <TabPanel value={selectedTab} index={1}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            🇬🇧 UK Payroll Features
          </Typography>
          <Typography variant="body2">
            • Automatic PAYE and National Insurance calculations<br />
            • Real-time tax code updates from HMRC<br />
            • Pension auto-enrollment compliance<br />
            • Student loan deductions<br />
            • P60 and P45 generation
          </Typography>
        </Alert>
        <Typography variant="h6">Payroll management coming soon...</Typography>
      </TabPanel>

      {/* Time Tracking Tab */}
      <TabPanel value={selectedTab} index={2}>
        <Typography variant="h6">Time tracking coming soon...</Typography>
      </TabPanel>

      {/* Leave Management Tab */}
      <TabPanel value={selectedTab} index={3}>
        <Typography variant="h6">Leave management coming soon...</Typography>
      </TabPanel>

      {/* AI Assistant Tab */}
      <TabPanel value={selectedTab} index={4}>
        <AIEmployeeAssistant
          employeeData={selectedEmployee}
          onEmployeeNumberSuggestion={(number) => {
            // Copy suggested number to clipboard and show notification
            navigator.clipboard.writeText(number);
            setSnackbar({
              open: true,
              message: `Employee number ${number} copied to clipboard!`,
              severity: 'success'
            });
          }}
          onValidationComplete={(result) => {
            // Show validation results in snackbar
            if (result.is_valid) {
              setSnackbar({
                open: true,
                message: 'Employee data validation passed!',
                severity: 'success'
              });
            } else {
              setSnackbar({
                open: true,
                message: `Validation failed: ${result.errors.join(', ')}`,
                severity: 'error'
              });
            }
          }}
        />
      </TabPanel>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => {
          if (selectedEmployee) {
            handleViewDetails(selectedEmployee);
            handleMenuClose();
          }
        }}>
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>

        <ProtectedComponent permission="view_payroll">
          <MenuItem onClick={handleMenuClose}>
            <PayrollIcon sx={{ mr: 1 }} />
            View Payroll
          </MenuItem>
        </ProtectedComponent>

        <ProtectedComponent permission="approve_time_entries">
          <MenuItem onClick={handleMenuClose}>
            <TimeIcon sx={{ mr: 1 }} />
            Time Entries
          </MenuItem>
        </ProtectedComponent>

        <ProtectedComponent permission="approve_leave_requests">
          <MenuItem onClick={handleMenuClose}>
            <LeaveIcon sx={{ mr: 1 }} />
            Leave Requests
          </MenuItem>
        </ProtectedComponent>
        <ProtectedComponent permission="edit_all_employees">
          <MenuItem
            onClick={() => {
              setTerminateDialogOpen(true);
              handleMenuClose();
            }}
            sx={{ color: 'warning.main' }}
          >
            <Pause sx={{ mr: 1 }} />
            Terminate Employment
          </MenuItem>
        </ProtectedComponent>
      </Menu>

      {/* Add Employee Dialog */}
      <AddEmployeeDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={handleAddEmployee}
      />


      {/* Employee Details Dialog */}
      <EmployeeDetailsDialog
        open={detailsDialogOpen}
        employee={selectedEmployee}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedEmployee(null); // Clear selected employee when dialog closes
        }}
        onEdit={handleEditEmployee}
        onDelete={handleDeleteEmployee}
        onTerminate={(id) => {
          setTerminateDialogOpen(true);
          setDetailsDialogOpen(false);
        }}
      />

      {/* Terminate Employee Dialog */}
      <TerminateEmployeeDialog
        open={terminateDialogOpen}
        employee={selectedEmployee}
        onClose={() => setTerminateDialogOpen(false)}
        onConfirm={handleTerminateEmployee}
      />

      {/* Import Wizard */}
      <ImportWizard
        open={showImportDialog}
        onClose={() => {
          setShowImportDialog(false);
          fetchEmployees(); // Refresh employee list after import
        }}
        entityType="employee"
        onImportComplete={(result) => {
          setSnackbar({
            open: true,
            message: `Successfully imported ${result.imported_count} employees!`,
            severity: 'success'
          });
        }}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
};

export default EmployeeManagement;
